import HttpStatus from 'http-status-codes'
import lodash from 'lodash'
import store from '@/store'
import types from '@/store/mutation-types'
import HTTP, { create, USER_DOMAIN_HEADER } from '@/utils/http'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import { handleUserContacts } from './util'

export async function getUserContact (id) {
  try {
    const response = await HTTP.get(`accounts/admins?id=${id}`)
    const account = response.data.data[0]
    const result = handleUserContacts(account)
    return result
  } catch (err) {
    return {}
  }
}

/**
 * Get vendor detail and sub-account emails by id and site code
 *
 * @export
 * @async
 * @param {String|Number} id
 * @param {String} siteCode
 * @param {boolean} [silent=false]
 * @returns {Object[]} [vendorDetail, vendorSubEmails]
 */
export async function getVendorDetailByIdAndSite (id, siteCode, silent = false) {
  try {
    const request = silent ? HTTP.silentGet : HTTP.get
    const response = await request(`accounts/vendor/detail/${id}/${siteCode}`)
    const account = response.data.data
    const vendorAdminAccount = (account.users || []).find(user => user.primary)
    const vendorSubAccounts = (account.users || []).filter(user => !user.primary)
    const vendorDetail = handleUserContacts(account)
    return [vendorDetail, vendorSubAccounts, vendorAdminAccount]
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getUser (id) {
  try {
    const response = await HTTP.get(`users/${id}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getEngineerProfile () {
  const response = await HTTP.get(`users/engineer/profile`)
  return response.data.data
}

export async function updateEngineerProfile (request) {
  try {
    const response = await HTTP.put(`users/engineer/profile`, request)
    return response.data.data
  } catch (error) {
    throw error
  }
}

export async function getExternalUserProfile () {
  let result
  try {
    const response = await HTTP.get('/external/users/profile')
    result = lodash.get(response, ['data', 'data'], {})
  } catch (error) {
    notification.handleResponseError(error)
    throw error
  }
  return result
}

export async function updateExternalUserProfile (requestData) {
  try {
    const response = await HTTP.put('/external/users/profile', requestData)
    const result = lodash.get(response, ['data', 'data'])
    return result
  } catch (error) {
    throw error
  }
}

export async function resetExternalUserPassword (username, domain) {
  let response
  try {
    response = await HTTP.post('/external/users/resetPassword', { username }, { headers: { [USER_DOMAIN_HEADER]: domain } })
  } catch (error) {
    notification.handleResponseError(error)
    throw error
  }
  return response
}

export async function getTenantById (id) {
  const response = await HTTP.get(`users/tenant/${id}`)
  return response.data.data
}

export async function getUserByUserRole (userRole) {
  const response = await HTTP.get(`users`, {
    params: {
      userRole,
    },
  })
  return response.data.data
}

export async function getUserTenantPerms (id) {
  const response = await HTTP.get(`users/${id}/tenant-perms`)
  return response.data.data
}

export async function updateUser (id, request) {
  try {
    const response = await HTTP.put(`/users/${id}`, request)
    return response
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function notifyUser (id) {
  try {
    await HTTP.post(`/users/notify/${id}`)
    notification.fireNotification(true, i18n.t('success'))
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

async function processApprovers (params, includeSelf) {
  const currentUser = store.getters['user/currentUser']
  if (!currentUser || !currentUser.username) {
    return []
  }
  const approvers = []
  const response = await HTTP.get('/users/approver', { params })
  if (response.data && response.data.data) {
    response.data.data.forEach(approver => {
      const username = approver.username
      if (username !== currentUser.username || includeSelf) {
        const option = { username, name: userHelper.getUserName(username, approver.displayName), id: approver.id }
        approvers.push(option)
      }
    })
  }
  return approvers
}

export async function getApprovers (includeSelf = false) {
  const currentTender = store.getters['tenders/currentTender']
  const tenderSite = (currentTender.purchaseRequisition || {}).siteId
  if (!tenderSite) {
    return []
  }
  const params = { site: tenderSite }
  const approvers = await processApprovers(params, includeSelf)
  return approvers
}

export async function getApproversOfBuilding (buildingId, rcType, tenancyId, tenancyType, includeSelf = false) {
  if (!buildingId || !rcType) {
    return []
  }
  const params = { buildingId, rcType, tenancyId, tenancyType }
  const approvers = await processApprovers(params, includeSelf)
  return approvers
}

export async function getSameBuildingUsers (buildingId, tenancyType, includeSelf = false, permTypes) {
  console.warn('Function getSameBuildingUsers is deprecated, consider using getSameBuildingUsersByPermission instead!')
  return getSameBuildingUsersByPermission({buildingId, tenancyType, includeSelf, permTypes})
}

export async function getSameBuildingUsersByPermission ({
  buildingId,
  tenancyId,
  tenancyType,
  includeSelf = false,
  permTypes,
  roleName,
}) {
  const users = []
  const currentUser = store.getters['user/currentUser']
  if (!currentUser || !currentUser.username || !buildingId) {
    return users
  }

  const response = await HTTP.get(`/buildings/${buildingId}/users`, {
    params: {
      tenancyId,
      tenancyType,
      permTypes,
      roleName,
    },
  })

  if (response.data && response.data.data) {
    response.data.data.users.forEach(user => {
      const username = user.username
      if (username !== currentUser.username || includeSelf) {
        users.push({
          username,
          name: userHelper.getUserName(username, user.displayName),
          id: user.id,
          email: user.email,
          phone: user.phone,
        })
      }
    })
  }
  return users
}

export async function checkStatus (keepAlive = false) {
  const keepAliveOption = keepAlive ? { headers: {'X-Keep-Alive': true} } : {}
  const customHttp = create(keepAliveOption)
  const response = await customHttp.head('status')
  if (!response || response.status !== HttpStatus.OK) {
    const err = new Error('Session Expired')
    err.response = { status: HttpStatus.UNAUTHORIZED }
    throw err
  }

  if (response.headers.authorization) {
    store.commit(`settings/${types.JSON_WEB_TOKEN}`, response.headers.authorization)
  }
}

export async function getUsersByPermsAndBuildings (permType, buildings = []) {
  try {
    const response = await HTTP.get(`/users/entitled`, { params: { permType, buildings: buildings.join(',') } })
    return response.data.data
  } catch (err) {
    return []
  }
}

export async function getVendorLoginDataCheck () {
  try {
    const response = await HTTP.silentGet('/vendor/data-checking')
    return response.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getVendorHasData () {
  try {
    const response = await HTTP.get('/vendor/has-data')
    return response.data
  } catch (err) {
    throw err
  }
}

export async function getAllUsersNotInCurrentArea () {
  try {
    const response = await HTTP.get('/accounts/all_users_not_in_current_area')
    return response.data
  } catch (err) {
    console.error(err)
    return []
  }
}

export async function inviteUserToRegion (users) {
  try {
    const response = await HTTP.post('/accounts/invite_to_region', {users})
    return response.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}
